'use client'

import React, { useState, useEffect, useRef } from 'react'
import { DROPDOWN_OPTIONS } from '@/app/threat-library/useThreatFilters'

// Importing types
import {
  ThreatFilterPanelProps,
  CustomInputProps,
  CustomSelectProps,
} from './ThreatFilterPanel.interface'
import { FilterState, FilterOption } from '@/app/threat-library/ThreatLibrary.interface'

import styles from './ThreatFilterPanel.module.scss'
import Image from 'next/image'

import FilterIcon from '@/public/assets/images/icon_filter_outline_red.svg'
import InputArrowRight from '@/public/assets/images/icon_input_arrow_right_red.svg'
import InputChevronDown from '@/public/assets/images/icon_input_angle_down_red.svg'
import ClearableTextInput from '@/src/components/commons/generics/ClearableTextInput/ClearableTextInput'

// --- Constants assumed from useThreatFilters.ts ---
const DEFAULT_ALL_VALUE = '' // API value for ALL/Both
const DEFAULT_ALL_LABEL = 'ALL'
const DEFAULT_BOTH_LABEL = 'Both' // Used by product filter
// ----------------------------------------------------

// =========================================================================
// 1. CUSTOM MULTI-SELECT COMPONENT (Dropdowns)
// =========================================================================
const CustomMultiSelect: React.FC<CustomSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  filterKey,
  isOpen,
  toggleDropdown,
}) => {
  const optionsRef = useRef<HTMLDivElement>(null)
  const [isScrollingActive, setIsScrollingActive] = useState(false)

  // Determine API default value and display label for the current filterKey
  const isProduct = filterKey === 'product'
  const defaultDisplayLabel = isProduct ? DEFAULT_BOTH_LABEL : DEFAULT_ALL_LABEL
  // Find the API value associated with the default label (should be DEFAULT_ALL_VALUE = '')
  const defaultApiValue =
    options.find((opt) => opt.label === defaultDisplayLabel)?.value || DEFAULT_ALL_VALUE

  //  Check if the state is currently set to the default (single item array containing the default API value: [''])
  const isCurrentlyDefault = selected.length === 1 && selected[0] === defaultApiValue

  // Get the actual option object that represents the default state for display purposes
  const currentDefaultOption = options.find((opt) => opt.value === defaultApiValue)

  const handleRemoveTag = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    valueToRemove: string
  ) => {
    e.stopPropagation() // Prevent the main button from toggling the dropdown

    // Check if the user is attempting to remove the default state tag
    if (valueToRemove === defaultApiValue) {
      return // Do nothing, the default state cannot be manually removed
    }

    // Filter out the value to be removed
    let newValues = selected.filter((v) => v !== valueToRemove)

    // Final check: If the new array is empty, revert to the field's default state (API value: [''])
    if (newValues.length === 0) {
      newValues = [defaultApiValue]
    }

    // Apply the new filtered array state
    onChange(newValues)
  }

  const handleRemoveTagKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    valueToRemove: string
  ) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return
    }
    e.preventDefault()
    handleRemoveTag(e, valueToRemove)
  }

  const handleClick = (value: string) => {
    let newValues: string[]

    const isDefaultValue = value === defaultApiValue
    const currentIsDefault = selected.length === 1 && selected.includes(defaultApiValue)

    if (isDefaultValue) {
      // RULE 1: If the user clicks the default option (API value: ''),
      // the result array MUST be only the default API value.
      newValues = [defaultApiValue]
    } else if (currentIsDefault) {
      // RULE 2: If default was active, clicking a custom item clears the default
      // and starts a new array with ONLY the clicked custom item.
      newValues = [value]
    } else if (selected.includes(value)) {
      // RULE 3: Deselect item
      newValues = selected.filter((v: string) => v !== value)
    } else {
      // RULE 4: Select item (multi-select)
      newValues = [...selected, value]
    }

    // Final Cleanup: If the new array is empty (last item was deselected), revert to the field's default.
    if (newValues.length === 0) {
      newValues = [defaultApiValue]
    }

    onChange(newValues)
  }

  // Maps selected values to their labels for display
  // filter out the default API value if other items are selected before mapping.
  const selectedDisplayOptions = options.filter((opt) => {
    // If the selection is the default state [''], we want this list to be empty for mapping.
    if (isCurrentlyDefault) return false

    // Otherwise, check if the option value is included in the selection
    return selected.includes(opt.value)
  })

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsScrollingActive(true)
      }, 300)

      return () => clearTimeout(timer)
    } else {
      setIsScrollingActive(false)
    }
  }, [isOpen])

  return (
    <div className={styles.multiSelectGroup}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.dropdownFieldSet}>
        {/* Main Dropdown Toggle Button Container */}
        <button
          className={`${styles.dropdownToggle} ${styles.tagContainer}`}
          onClick={toggleDropdown}
          type="button"
        >
          {isCurrentlyDefault ? (
            // Render the single default tag when isCurrentlyDefault is true
            <div
              className={`${styles.selectedTag} py-[4px]! px-[12px]!`}
              role="button"
              tabIndex={0}
              aria-label={`Remove filter: ${currentDefaultOption?.label || 'ALL'}`}
              onClick={(e) => handleRemoveTag(e, currentDefaultOption?.value || defaultApiValue)}
              onKeyDown={(e) =>
                handleRemoveTagKeyDown(e, currentDefaultOption?.value || defaultApiValue)
              }
            >
              {/* Render the correct default label */}
              <span className={styles.tagLabel}>{currentDefaultOption?.label || 'ALL'}</span>
            </div>
          ) : (
            // Render selected custom tags (with X)
            selectedDisplayOptions.map((opt) => (
              <div
                key={opt.value}
                className={styles.selectedTag}
                role="button"
                tabIndex={0}
                aria-label={`Remove filter: ${opt.label}`}
                onClick={(e) => handleRemoveTag(e, opt.value)}
                onKeyDown={(e) => handleRemoveTagKeyDown(e, opt.value)}
              >
                <span className={styles.closeIcon}>&times;</span>
                <span className={styles.selectedOption}>{opt.label}</span>
              </div>
            ))
          )}
          <div className={styles.toggleIcon}>
            <Image
              src={InputChevronDown.src}
              alt="Dropdown Toggle"
              width={10}
              height={15}
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s',
              }}
            />
          </div>
        </button>

        {/* Hidden Options Container: Toggles visibility on button click */}
        <div
          ref={optionsRef}
          className={`
                    ${styles.optionsContainer} 
                    ${isOpen ? styles.open : ''} 
                    ${isScrollingActive ? styles.overflowActive : ''} 
                `}
        >
          {options.map((opt: FilterOption) => (
            <button
              key={opt.value}
              type="button"
              // Add disabled attribute for header options
              disabled={opt.isHeader}
              className={
                // Add a class for non-selectable headers for specific styling
                `${opt.isSubOption ? 'pl-[24px]!' : ''} ` +
                `${opt.isHeader ? styles.optionHeader : ''} ` +
                (selected.includes(opt.value) ? styles.optionSelected : styles.optionDefault)
              }
              // Only call handleClick if the option is not a header
              onClick={() => !opt.isHeader && handleClick(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// =========================================================================
// 2. CUSTOM INPUT COMPONENT (Accepts onFocus prop)
// =========================================================================
const CustomInput: React.FC<CustomInputProps> = ({
  label,
  value,
  onChange,
  icon,
  onFocus,
  autoFocus,
  clearable,
}) => (
  <div className={`${styles.inputGroup} ${styles.inputGroupRelative}`}>
    <span className={styles.fieldLabel}>{label}</span>
    <ClearableTextInput
      value={value}
      onChange={onChange}
      placeholder={label}
      onFocus={onFocus}
      autoFocus={autoFocus}
      clearable={clearable}
      clearButtonAriaLabel={`Clear ${label}`}
      containerClassName={styles.inputWrapper}
      trailingAdornment={
        <span className={styles.inputIcon}>
          <Image src={icon as string} alt="" aria-hidden="true" width={10} height={10} />
        </span>
      }
    />
  </div>
)

// =========================================================================
// 3. MAIN THREAT FILTER PANEL COMPONENT (Handles Global State)
// =========================================================================
const ThreatFilterPanel: React.FC<ThreatFilterPanelProps> = ({
  isMobile,
  filters,
  updateFilter,
  resetFilters,
  isPanelOpen,
  resultsCount,
}) => {
  const isDesktop = !isMobile

  // Ref to anchor the entire filter panel (used for outside clicks)
  const panelRef = useRef<HTMLDivElement>(null)

  // Track the key of the currently open dropdown
  const [activeDropdownKey, setActiveDropdownKey] = useState<keyof FilterState | null>(null)

  // Closes the current dropdown (used by external click and inputs)
  const closeAllDropdowns = () => {
    setActiveDropdownKey(null)
  }

  // Function to toggle a dropdown, closing others
  const toggleDropdown = (key: keyof FilterState) => {
    setActiveDropdownKey((prevKey) => (prevKey === key ? null : key))
  }

  // useEffect: Closes dropdown when clicking outside the panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click occurred outside the entire filter panel area
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // If a dropdown is open, close it
        if (activeDropdownKey !== null) {
          closeAllDropdowns()
        }
      }
    }

    // Attach listener globally
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      // Cleanup listener on unmount
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdownKey])

  if (isMobile && !isPanelOpen) return null

  // Manually define the A-Z Listing array to keep 'Special Characters' as one element
  const A_Z_LISTING = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
    '0-9',
    'Special Characters',
  ]

  const handleAZSelect = (value: string) => {
    const currentValues = filters.azListing
    let newValues

    if (currentValues.includes(value)) {
      newValues = currentValues.filter((v) => v !== value)
    } else {
      newValues = [...currentValues, value]
    }

    updateFilter('azListing', newValues)
  }

  const handleAZSelectKeyDown = (
    e: React.KeyboardEvent<HTMLSpanElement>,
    value: string
  ) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return
    }
    e.preventDefault()
    handleAZSelect(value)
  }

  const filterUI = (
    <div className={styles.filterBody}>
      {/* Clear Filters */}
      <button className={styles.clearFilters} onClick={resetFilters}>
        <span>
          <Image
            src={FilterIcon.src}
            alt="Dropdown Toggle"
            width={12}
            height={13}
            className={`${styles.clearFilterIcon} inline-block! align-middle mr-[4px]`}
          />{' '}
          Clear All Filters
        </span>
      </button>

      {/* Threat Name Input */}
      <CustomInput
        label="Threat Name"
        value={filters.threatName}
        onChange={(v: string) => updateFilter('threatName', v)}
        icon={InputArrowRight.src}
        onFocus={closeAllDropdowns}
        autoFocus={isDesktop}
        clearable
      />

      {/* Signature ID Input */}
      <CustomInput
        label="Signature ID"
        value={filters.signatureId}
        onChange={(v: string) => updateFilter('signatureId', v)}
        icon={InputArrowRight.src}
        onFocus={closeAllDropdowns}
      />

      {/* Category Select (Controls passed) */}
      <CustomMultiSelect
        label="Category"
        filterKey="category"
        options={DROPDOWN_OPTIONS.category}
        selected={filters.category}
        onChange={(v: string[]) => updateFilter('category', v)}
        isOpen={activeDropdownKey === 'category'}
        toggleDropdown={() => toggleDropdown('category')}
      />

      {/* Threat Severity Select */}
      <CustomMultiSelect
        label="Threat Severity"
        filterKey="severity"
        options={DROPDOWN_OPTIONS.severity}
        selected={filters.severity}
        onChange={(v: string[]) => updateFilter('severity', v)}
        isOpen={activeDropdownKey === 'severity'}
        toggleDropdown={() => toggleDropdown('severity')}
      />

      {/* Direction Select */}
      <CustomMultiSelect
        label="Direction"
        filterKey="direction"
        options={DROPDOWN_OPTIONS.direction}
        selected={filters.direction}
        onChange={(v: string[]) => updateFilter('direction', v)}
        isOpen={activeDropdownKey === 'direction'}
        toggleDropdown={() => toggleDropdown('direction')}
      />

      {/* Engines Select */}
      <CustomMultiSelect
        label="Engines"
        filterKey="engines"
        options={DROPDOWN_OPTIONS.engines}
        selected={filters.engines}
        onChange={(v: string[]) => updateFilter('engines', v)}
        isOpen={activeDropdownKey === 'engines'}
        toggleDropdown={() => toggleDropdown('engines')}
      />

      {/* Product Select (Uses 'Both' as default) */}
      <CustomMultiSelect
        label="Product"
        filterKey="product"
        options={DROPDOWN_OPTIONS.product}
        selected={filters.product}
        onChange={(v: string[]) => updateFilter('product', v)}
        isOpen={activeDropdownKey === 'product'}
        toggleDropdown={() => toggleDropdown('product')}
      />

      {/* A-Z Listing */}
      <div className={styles.azListing}>
        <span className={styles.fieldLabel}>A-Z Listing</span>
        <div className={styles.azLetters}>
          {A_Z_LISTING.map((letter) => (
            <span
              key={letter}
              role="button"
              tabIndex={0}
              aria-label={`${filters.azListing.includes(letter) ? 'Deselect' : 'Select'} ${letter}`}
              aria-pressed={filters.azListing.includes(letter)}
              className={`${filters.azListing.includes(letter) ? styles.selected : ''} ${letter.toUpperCase() === 'SPECIAL CHARACTERS' ? styles.specialCharButton : ''}`}
              onClick={() => handleAZSelect(letter)}
              onKeyDown={(e) => handleAZSelectKeyDown(e, letter)}
            >
              {letter}
            </span>
          ))}
        </div>
      </div>
    </div>
  )

  const txtResults = 'Search Result: '

  return (
    <div
      ref={panelRef}
      className={`${styles.filterPanel} ${isDesktop ? styles.desktop : styles.mobile} ${isPanelOpen ? styles.open : ''}`}
    >
      {/* Mobile Close Button moved to /threat-library/page.tsx */}
      <div
        className={`${styles.resultsSummary} ${styles.resultsSummaryMobile} md:hidden mb-[20px] pb-[20px] text-center uppercase text-[16px] font-medium ${styles.topResults}`}
      >
        {txtResults} {resultsCount.toLocaleString()}
      </div>
      {filterUI}
      <div
        className={`${styles.resultsSummary} ${styles.resultsSummaryMobile} md:hidden mt-[20px] pt-[20px] text-center uppercase text-[16px] font-medium ${styles.bottomResults}`}
      >
        {txtResults} {resultsCount.toLocaleString()}
      </div>
    </div>
  )
}

export default ThreatFilterPanel
